import type { FC, MouseEvent } from 'react'
import React, { useState } from 'react';
import { alpha, styled } from '@mui/system';
import {
  AppBar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
} from '@mui/material';
import {
  Twitter as TwitterIcon,
  FacebookRounded as FacebookIcon,
  Instagram as InstagramIcon
} from '@mui/icons-material';
import Icon from './Icon';
import Link from './Link';

const pages = [
  { label: 'Home', icon: 'home', path: '/' },
  { label: 'About', icon: 'ghost', path: '/about' },
  { label: 'Projects', icon: 'book-dead', path: '/projects' }
];

const socials = [
  { icon: <FacebookIcon />, path: 'https://facebook.com/scawwyhowwowtheatre/' },
  { icon: <InstagramIcon />, path: 'https://instagram.com/scawwyhowwowtheatre/' },
  { icon: <TwitterIcon />, path: 'https://twitter.com/unredpod' },
];

const Profile = styled('img')(
  ({ theme }) => ({
    border: 1,
    borderColor: 'black',
    borderRadius: theme.shape.borderRadius,
    height: 32, width: 32,
  })
);

interface AppBarProps {
  hideProfile?: boolean;
}

const AppBarComponent: FC<AppBarProps> = (props) => {
  const { hideProfile = false } = props;
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>): void => {
    setAnchor(event.currentTarget);
  };

  const handleCloseNavMenu = (): void => {
    setAnchor(null);
  };

  return (
    <AppBar
      position="sticky"
      sx={{ backgroundColor: '#000000' }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          flexFlow: 'row nowrap',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            sx={{ color: 'white', position: 'relative' }}
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleOpenNavMenu}
          >
            <Icon
              name="bars"
              sx={{
                transition: 'all 0.25s',
                opacity: anchor ? 0 : 1,
              }}
            />
            <Icon
              name="xmark"
              sx={{
                transition: 'all 0.25s',
                opacity: anchor ? 1 : 0,
                position: 'absolute'
              }}
            />
          </IconButton>
          {!hideProfile && (
            <IconButton
              component={Link}
              to="/"
            >
              <Profile src="/static/profile.jpg" />
            </IconButton>
          )}
          <Menu
            id="menu-appbar"
            anchorEl={anchor}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left'
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left'
            }}
            open={Boolean(anchor)}
            onClose={handleCloseNavMenu}
            sx={{
              display: { xs: 'block', md: 'none' },
            }}
          >
            {pages.map((page) => (
              <MenuItem
                key={page.label}
                component={Link}
                to={page.path}
                sx={{ width: '100vw' }}
              >
                <ListItemIcon>
                  <Icon name={page.icon} />
                </ListItemIcon>
                <ListItemText primary={page.label} />
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3 }}>
          {!hideProfile && (
            <IconButton
              component={Link}
              to="/"
            >
              <Profile src="/static/profile.jpg" />
            </IconButton>
          )}
          {pages.map((page) =>  (
            <Button
              sx={{
                color: 'white',
                my: 2,
                '&:hover': {
                  backgroundColor: (theme) => alpha('#ffffff', theme.palette.action.hoverOpacity * 4)
                }
              }}
              key={page.icon}
              component={Link}
              startIcon={<Icon name={page.icon} />}
              to={page.path}
              color="primary"
            >
              {page.label}
            </Button>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {socials.map((page) =>  (
            <IconButton
              sx={{
                color: 'white',
                my: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  backgroundColor: (theme) => alpha('#ffffff', theme.palette.action.hoverOpacity * 4)
                }
              }}
              key={page.path}
              component="a"
              href={page.path}
            >
              {page.icon}
            </IconButton>
          ))}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AppBarComponent;
